import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";

interface PackagesState {
  error: string;
  loading: boolean;
  packages: Package[];
  getPackages: (onSuccess?: (response: Package[]) => void) => void;
  getPackage: (
    packageId: string,
    onSuccess?: (response: Package) => void
  ) => void;
  updatePackage: (
    packageData: Package,
    onSuccess?: (response: Package) => void
  ) => void;
  addPackage: (
    packageData: Package,
    onSuccess?: (newPackage: Package) => void
  ) => void;
  deletePackage: (
    packageId: string,
    onSuccess?: () => void
  ) => void;
  markBuyPackageAsPaid: (
    patientId: string,
    transactionId: string,
    onSuccess?: () => void
  ) => void;
  getPatientCredits: (
    patientId: string,
    onSuccess?: (response: PatientCreditsResponse) => void
  ) => void;
  getPatientCreditsList: (
    patientId: string,
    onSuccess?: (response: PatientCreditsListResponse) => void
  ) => void;
  resetError: VoidFunction;
}

const usePackages = (): PackagesState => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [error, setError] = useState<string>("");

  const {
    getData: getPackagesRequest,
    loading: getPackagesLoading,
  } = useApi<Package[]>();

  const {
    getData: getPackageRequest,
    loading: getPackageLoading,
  } = useApi<Package>();

  const {
    postData: addPackageRequest,
    loading: addPackageLoading,
  } = useApi<Package>();

  const {
    putData: updatePackageRequest,
    loading: updatePackageLoading,
  } = useApi<Package>();

  const {
    deleteData: deletePackageRequest,
    loading: deletePackageLoading,
  } = useApi();

  const {
    getData: markBuyPackageAsPaidRequest,
    loading: markBuyPackageAsPaidLoading,
  } = useApi();

  const {
    getData: getPatientCreditsRequest,
    loading: getPatientCreditsRequestLoading,
  } = useApi();

  const {
    getData: getPatientCreditsListRequest,
    loading: getPatientCreditsListRequestLoading,
  } = useApi();

  const getPackages = async (onSuccess?: (response: Package[]) => void) => {
    setError("");
    await getPackagesRequest(
      API_ROUTES.PACKAGES.base,
      (response: Package[]) => {
        setPackages(response);
        onSuccess?.(response);
      },
      setError
    );
  };

  const getPackage = async (
    packageId: string,
    onSuccess?: (response: Package) => void
  ) => {
    setError("");
    await getPackageRequest(
      API_ROUTES.PACKAGES.byId(packageId),
      (response: Package) => {
        onSuccess?.(response);
      },
      setError
    );
  };

  const addPackage = async (
    packageData: Package,
    onSuccess?: (newPackage: Package) => void
  ) => {
    setError("");
    await addPackageRequest(
      API_ROUTES.PACKAGES.base,
      packageData,
      (response: Package) => {
        onSuccess?.(response);
        // Refresh the packages list
        getPackages();
      },
      setError
    );
  };

  const updatePackage = async (
    packageData: Package,
    onSuccess?: (response: Package) => void
  ) => {
    setError("");
    // Omit non-updatable fields if present in the object (e.g., createdAt, updatedAt, index)
    const { createdAt, updatedAt, index, ...payload } = (packageData as unknown) as Record<string, any>;
    await updatePackageRequest(
      API_ROUTES.PACKAGES.byId(packageData.id),
      payload as Package,
      (response: Package) => {
        onSuccess?.(response);
        // Refresh the packages list
        getPackages();
      },
      setError
    );
  };

  const deletePackage = async (
    packageId: string,
    onSuccess?: () => void
  ) => {
    setError("");
    await deletePackageRequest(
      API_ROUTES.PACKAGES.byId(packageId),
      {},
      () => {
        onSuccess?.();
        // Refresh the packages list
        getPackages();
      },
      setError
    );
  };

  const markBuyPackageAsPaid = async (
    patientId: string,
    transactionId: string,
    onSuccess?: () => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PATIENT.patientBuyPackageSuccess
      .replace("{{PATIENT_ID}}", patientId)
      .replace("{{TRANSACTION_ID}}", transactionId);
    await markBuyPackageAsPaidRequest(
      endPoint,
      () => {
        onSuccess?.();
        // Refresh the packages list
        getPackages();
      },
      setError
    );
  };

  const getPatientCredits = async (
    patientId: string,
    onSuccess?: (response: PatientCreditsResponse) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PATIENT.getPatientCredits
      .replace("{{PATIENT_ID}}", patientId)
    await getPatientCreditsRequest(
      endPoint,
      (response: PatientCreditsResponse) => {
        onSuccess?.(response);
      },
      setError
    );
  };

  const getPatientCreditsList = async (
    patientId: string,
    onSuccess?: (response: PatientCreditsListResponse) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PATIENT.getPatientCreditsList
      .replace("{{PATIENT_ID}}", patientId)
    await getPatientCreditsListRequest(
      endPoint,
      (response: PatientCreditsListResponse) => {
        onSuccess?.(response);
      },
      setError
    );
  };

  const resetError = () => {
    setError("");
  };

  return {
    error,
    loading:
      getPackagesLoading ||
      getPackageLoading ||
      addPackageLoading ||
      updatePackageLoading ||
      deletePackageLoading ||
      markBuyPackageAsPaidLoading ||
      getPatientCreditsRequestLoading || 
      getPatientCreditsListRequestLoading,
    packages,
    getPackages,
    getPackage,
    updatePackage,
    addPackage,
    deletePackage,
    resetError,
    markBuyPackageAsPaid,
    getPatientCredits,
    getPatientCreditsList,
  };
};

export default usePackages;